'use client';

import Link from 'next/link';
import { useState, type MouseEvent } from 'react';
import {
  Archive,
  ArrowRight,
  Award,
  Briefcase,
  Building2,
  Calendar,
  Compass,
  Download,
  FileCheck2,
  Heart,
  Image as ImageIcon,
  Map,
  MapPin,
  MessageCircle,
  Share,
  ShieldCheck,
  SquarePlus,
  UserRound,
} from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { usePwaInstall } from '@/hooks/usePwaInstall';

export default function HeroSection() {
  const { safeT } = useTranslation();
  const reducedMotion = useReducedMotion();
  const { isStandalone, canInstall, isIOS, isIOSSafari, promptInstall } = usePwaInstall();
  const [showInstallGuidance, setShowInstallGuidance] = useState(false);
  // Installation itself (not the native beforeinstallprompt event, which is a
  // Chromium-only signal that often hasn't fired yet on a first visit) is what
  // gates this: hidden only once actually running standalone, or on an iOS
  // browser that isn't Safari (Chrome/Firefox-on-iOS cannot install PWAs at
  // all -- that's an Apple platform restriction, not a detection gap). Every
  // other case -- including "Android/Chromium but beforeinstallprompt hasn't
  // fired yet" -- still shows the action; see the fallback-tip branch below.
  const showInstallAction = !isStandalone && !(isIOS && !isIOSSafari);
  const needsGuidance = !canInstall; // iOS Safari steps, or a generic browser-menu fallback tip

  const scrollToShowcase = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById('product-showcase');
    if (!target) return;
    event.preventDefault();
    // 'auto' defers to the page's own CSS scroll-behavior (smooth site-wide), so it would
    // still animate under reduced motion. 'instant' forces a real jump regardless of CSS.
    target.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  };

  return (
    <section className="landing-hero relative isolate overflow-hidden pt-20 sm:pt-24 xl:pt-28">
      <div
        className="landing-hero-aurora pointer-events-none absolute inset-0 -z-10"
        aria-hidden="true"
      />

      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-9 sm:px-8 sm:py-12 lg:min-h-[84dvh] lg:grid-cols-[minmax(0,1.05fr)_minmax(380px,.8fr)] lg:items-center lg:gap-14 lg:px-10 lg:py-16">
        <div className="max-w-2xl">
          <p className="landing-hero-eyebrow mb-5 max-w-full px-3 py-1.5">
            <Archive size={14} aria-hidden="true" />
            {safeT(
              'landing_hero.eyebrow' as any,
              'A living record of stories, memories, and human achievement'
            )}
          </p>

          <h1 className="font-serif text-[clamp(2.25rem,3.6vw,3.5rem)] leading-[1.08] tracking-[-0.03em] text-slate-950">
            Your life is more than a feed.{' '}
            <span className="landing-hero-title-accent">It is a living record.</span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-7 text-slate-700 sm:text-lg sm:leading-8">
            Ertzyx brings together the stories, memories, people, places, milestones, media,
            achievements and recognition that shape a human life. Preserve what matters through the
            Preservation Layer, and add evidence, witnesses, validation and institutional
            verification through the Trust Layer when credibility matters.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              href="/register"
              className="public-button landing-hero-cta--primary min-h-12 px-6 py-3"
            >
              <UserRound size={17} aria-hidden="true" />
              Begin your living record
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              href="#product-showcase"
              onClick={scrollToShowcase}
              className="public-button landing-hero-cta--secondary min-h-12 px-6 py-3"
            >
              <Compass size={17} aria-hidden="true" />
              {safeT('landing_hero.cta_explore' as any, 'See Ertzyx in action')}
            </Link>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link
              href="/for-institutions"
              className="landing-hero-utility inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4"
            >
              <Building2 size={14} className="text-[var(--public-amethyst)]" aria-hidden="true" />
              {safeT('landing_hero.cta_institution' as any, 'Ertzyx for institutions')}
            </Link>

            {showInstallAction && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (canInstall) void promptInstall();
                    else setShowInstallGuidance((open) => !open);
                  }}
                  aria-expanded={needsGuidance ? showInstallGuidance : undefined}
                  className="landing-hero-utility landing-hero-install-link inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4"
                >
                  <span className="landing-hero-install-icon" aria-hidden="true">
                    <Download size={13} />
                  </span>
                  {safeT('pwa.install_action' as any, 'Install Ertzyx')}
                </button>

                {needsGuidance && showInstallGuidance && (
                  <div
                    role="dialog"
                    aria-label={safeT('pwa.install_action' as any, 'Install Ertzyx')}
                    className="landing-install-dialog absolute left-0 top-full z-10 mt-2 w-56 rounded-xl border p-3.5 text-left"
                  >
                    {isIOSSafari ? (
                      <ol className="flex flex-col gap-1.5 text-xs font-medium text-slate-800">
                        <li className="flex items-center gap-2">
                          <Share size={12} className="text-emerald-800" aria-hidden="true" />
                          {safeT('pwa.ios_step_share' as any, 'Tap Share')}
                        </li>
                        <li className="flex items-center gap-2">
                          <SquarePlus size={12} className="text-emerald-800" aria-hidden="true" />
                          {safeT('pwa.ios_step_add_home' as any, 'Tap Add to Home Screen')}
                        </li>
                        <li className="flex items-center gap-2">
                          <Download size={12} className="text-emerald-800" aria-hidden="true" />
                          {safeT('pwa.ios_step_add' as any, 'Tap Add')}
                        </li>
                      </ol>
                    ) : (
                      <p className="text-xs font-medium leading-relaxed text-slate-800">
                        {safeT(
                          'pwa.generic_install_tip' as any,
                          'Look for "Install app" or "Add to Home Screen" in your browser\'s menu.'
                        )}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowInstallGuidance(false)}
                      className="mt-2.5 text-[11px] font-semibold text-slate-500 underline underline-offset-2 hover:text-slate-700"
                    >
                      {safeT('common.close' as any, 'Close')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <ul className="mt-8 grid max-w-xl gap-x-5 gap-y-3 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <Archive className="mt-0.5 shrink-0 text-emerald-800" size={16} aria-hidden="true" />
              {safeT(
                'landing_hero.feature_1' as any,
                'Preserve memories, family history, and milestones'
              )}
            </li>
            <li className="flex items-start gap-2">
              <Award className="mt-0.5 shrink-0 text-emerald-800" size={16} aria-hidden="true" />
              {safeT(
                'landing_hero.feature_2' as any,
                'Celebrate achievements and connect through stories'
              )}
            </li>
            <li className="flex items-start gap-2">
              <FileCheck2
                className="mt-0.5 shrink-0 text-emerald-800"
                size={16}
                aria-hidden="true"
              />
              {safeT('landing_hero.feature_3' as any, 'Add evidence and witnesses when it matters')}
            </li>
          </ul>
        </div>

        <div
          className="relative mx-auto w-full max-w-xl lg:max-w-none"
          aria-label="Example story and connected records on Ertzyx"
        >
          <div className="landing-record-aura absolute -inset-5 -z-10 rounded-[32px] blur-2xl" />

          <article className="landing-record-card overflow-hidden rounded-[14px]">
            <div className="flex items-center gap-3 px-5 pt-5">
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=120&q=80"
                alt=""
                className="h-11 w-11 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-950">Elena Torres</p>
                <p className="truncate text-xs text-slate-500">@elena.torres</p>
              </div>
              <span className="landing-achievement-chip ml-auto inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11px] font-bold">
                Achievement
              </span>
            </div>

            <div className="px-5 pt-4">
              <h2 className="font-serif text-xl leading-snug text-slate-950">
                Finished my first marathon after two years of recovery
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Three surgeries, a knee doctors said might not hold, and one very early alarm clock
                every single day this year, until I saw the finish line.
              </p>
              <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={12} aria-hidden="true" />
                  Oct 2025
                </span>
                <span aria-hidden="true">&middot;</span>
                <span className="inline-flex items-center gap-1">
                  <MapPin size={12} aria-hidden="true" />
                  Austin, Texas
                </span>
                <span aria-hidden="true">&middot;</span>
                <span className="inline-flex items-center gap-1">
                  <ImageIcon size={12} aria-hidden="true" />
                  Photo &amp; video
                </span>
              </p>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Heart size={13} className="text-rose-500" aria-hidden="true" />
                128
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MessageCircle size={13} aria-hidden="true" />
                24 comments
              </span>
              <span className="inline-flex items-center gap-1.5 text-slate-400">
                <UserRound size={13} aria-hidden="true" />
                Witnessed by 2 friends
              </span>
            </div>
          </article>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="landing-mini-card landing-mini-card--journey rounded-[14px] px-4 py-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-800">
                <Map size={12} aria-hidden="true" />
                Journey Map
              </span>
              <p className="mt-1.5 text-xs leading-5 text-slate-600">
                12 milestones charted across three cities
              </p>
            </div>
            <div className="landing-mini-card landing-mini-card--family rounded-[14px] px-4 py-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                <Archive size={12} aria-hidden="true" />
                Family Ledger
              </span>
              <p className="mt-1.5 text-xs leading-5 text-slate-600">
                Grandmother&apos;s recipe box, preserved
              </p>
            </div>
            <div className="landing-mini-card landing-mini-card--career rounded-[14px] px-4 py-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--public-amethyst)]">
                <Briefcase size={12} aria-hidden="true" />
                Career milestone
              </span>
              <p className="mt-1.5 text-xs leading-5 text-slate-600">Promoted to senior engineer</p>
            </div>
          </div>

          <div className="landing-trust-strip mt-3 flex items-center justify-center gap-2 rounded-full px-4 py-2 text-center text-[11px] font-semibold text-slate-600">
            <ShieldCheck size={13} className="shrink-0 text-emerald-700" aria-hidden="true" />
            Optional: institution-reviewed records can attach to any story
          </div>
        </div>
      </div>
    </section>
  );
}
